import React from "react";
import { Helmet } from "react-helmet-async";

export default function Seo({ 
  title, 
  description, 
  image, 
  url, 
  type = "website",
  // For QAPage schema
  question = null,
  answers = [],
  category = null,
  dateCreated = null,
  // For breadcrumbs
  breadcrumbs = null
}) {
  const baseUrl = "https://askincog.com";
  const siteName = "AskIncog – The Incognito Diary";
  const twitterHandle = "@askincog247";
  
  // Default values
  const defaultTitle = "AskIncog – Bangalore's Anonymous Community | Ask Anything, Stay Invisible";
  const defaultDescription = "AskIncog is Bangalore's anonymous community. Ask real questions, get real answers — no login, no judgment, no names. Join thousands already asking.";
  const defaultImage = `${baseUrl}/og-image.jpg`;
  
  // Build safe values
  const safeTitle = title ? `${title} – AskIncog` : defaultTitle;
  const safeDesc = description || defaultDescription;
  const safeImage = image || defaultImage;
  const safeUrl = url ? `${baseUrl}${url}` : baseUrl;

  // Organization Schema (for homepage)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AskIncog",
    "url": baseUrl,
    "logo": `${baseUrl}/logo.png`,
    "description": "Bangalore's anonymous community Q&A platform. Ask anything, stay invisible.",
    "sameAs": [
      "https://instagram.com/askincog24",
      "https://x.com/askincog247",
      "https://www.youtube.com/channel/UCoL-ILD6ESPXHbsurJqxmng"
    ]
  };

  // WebSite Schema (for homepage - enables sitelinks search)
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AskIncog",
    "url": baseUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/browse?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  // QAPage Schema (for question pages)
  const qaPageSchema = question ? {
    "@context": "https://schema.org",
    "@type": "QAPage",
    "mainEntity": {
      "@type": "Question",
      "name": question.title || question.body?.slice(0, 100),
      "text": question.body,
      "answerCount": answers.length,
      "dateCreated": dateCreated || question.created_at,
      ...(answers.length > 0 && {
        "acceptedAnswer": {
          "@type": "Answer",
          "text": answers[0]?.answer_text?.slice(0, 500),
          "upvoteCount": answers[0]?.upvotes || 0,
          "dateCreated": answers[0]?.created_at
        }
      })
    }
  } : null;

  // CollectionPage Schema (for category pages)
  const collectionSchema = category ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `Anonymous ${category} Questions – Bangalore | AskIncog`,
    "description": `Real anonymous ${category} questions from Bangalore. No login needed.`,
    "url": `${baseUrl}/browse?category=${encodeURIComponent(category)}`
  } : null;

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`
    }))
  } : null;

  // Determine which schemas to include
  const isHomepage = !url || url === '/';
  const isQuestionPage = !!question;
  const isCategoryPage = !!category && !question;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{safeTitle}</title>
      <meta name="description" content={safeDesc} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={safeUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={isQuestionPage ? "article" : type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:url" content={safeUrl} />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={safeDesc} />
      <meta property="og:image" content={safeImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={safeDesc} />
      <meta name="twitter:image" content={safeImage} />
      
      {/* JSON-LD Structured Data */}
      {isHomepage && (
        <>
          <script type="application/ld+json">
            {JSON.stringify(organizationSchema)}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(websiteSchema)}
          </script>
        </>
      )}
      
      {isQuestionPage && qaPageSchema && (
        <script type="application/ld+json">
          {JSON.stringify(qaPageSchema)}
        </script>
      )}
      
      {isCategoryPage && collectionSchema && (
        <script type="application/ld+json">
          {JSON.stringify(collectionSchema)}
        </script>
      )}
      
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
}
